/**
 * NDA Template Master · list + versioning lifecycle screen.
 *
 * Data model
 * ----------
 * A template row is uniquely identified by (template_code, version).
 * ``status`` follows a controlled lifecycle: ``Draft → Published → Retired``.
 *
 *  * Draft       — editable freely.
 *  * Published   — ONLY editable if never referenced by an NDA.  Once
 *                  used, it becomes fully immutable (Clone as New Version
 *                  is the only path forward).
 *  * Retired     — historical; hidden from auto-selection but still
 *                  used to render existing NDAs' frozen snapshots.
 *
 * Gate: ``nda_template:write`` (super_admin by default).
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
    Plus, PencilSimple, Copy, CheckCircle, Prohibit, Package,
    Funnel, Scales, ArrowRight, Sparkle, Shield,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { formatDate } from "../../lib/format";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { DialogFooter } from "../../components/ui/dialog";
import FormDialog, { FormDialogCancel } from "../../components/FormDialog";
import PageHeader from "../../components/layout/PageHeader";
import EmptyState from "../../components/EmptyState";
import StatusBadge from "../../components/StatusBadge";
import { toast } from "sonner";

const NDA_TYPES = ["Data", "Software"];

const STATUS_BADGE_MAP = {
    "Draft": { status: "Draft" },
    "Published": { status: "Active" },
    "Retired": { status: "Cancelled" },
};

function NewTemplateDialog({ open, onOpenChange, onCreated }) {
    const blank = {
        template_code: "",
        template_name: "",
        nda_type: "Data",
        default_purpose:
            "To facilitate discussions and evaluation between the Parties in connection with exploring potential business opportunities.",
        first_clause_heading: "Purpose",
        first_clause_body:
            "The Parties are exploring a potential business relationship and may share Confidential Information solely for evaluation of a possible business opportunity.  Nothing in this Agreement grants any commercial right, licence or entitlement to the Recipient.",
    };
    const [form, setForm] = useState(blank);
    const [submitting, setSubmitting] = useState(false);
    useEffect(() => { if (open) setForm(blank); }, [open]);

    const submit = async () => {
        if (!form.template_code.trim() || !form.template_name.trim()) {
            toast.error("Template code and name are required");
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await api.post("/billing/nda-template/create", {
                template_code: form.template_code.trim().toUpperCase(),
                template_name: form.template_name.trim(),
                nda_type: form.nda_type,
                version: 1,
                default_purpose: form.default_purpose.trim(),
                clauses: [{
                    heading: form.first_clause_heading.trim(),
                    body: form.first_clause_body.trim(),
                    display_order: 10,
                    is_included: true,
                }],
            });
            toast.success(`Created ${data.template_code} v${data.version} as Draft`);
            onCreated?.(data);
            onOpenChange(false);
        } catch (err) {
            toast.error(formatApiError(err, "Create failed"));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <FormDialog
            open={open}
            onOpenChange={onOpenChange}
            title="New NDA Template"
            description="Create a Draft template.  You can add / reorder clauses on the editor page.  Publish only when Legal is satisfied — clauses become immutable once the template is used by an NDA."
            testId="nda-template-new-dialog"
            widthClass="max-w-2xl"
        >
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <Label>Template Code *</Label>
                    <Input value={form.template_code} onChange={(e) => setForm((f) => ({ ...f, template_code: e.target.value }))} placeholder="e.g. DATA_NDA_V2" data-testid="nda-template-new-code" />
                </div>
                <div>
                    <Label>Template Name *</Label>
                    <Input value={form.template_name} onChange={(e) => setForm((f) => ({ ...f, template_name: e.target.value }))} placeholder="e.g. REEDOS Data NDA (Standard v2)" data-testid="nda-template-new-name" />
                </div>
                <div>
                    <Label>NDA Type</Label>
                    <select value={form.nda_type} onChange={(e) => setForm((f) => ({ ...f, nda_type: e.target.value }))} className="mt-1 w-full h-9 px-3 text-sm border border-slate-200 rounded-md bg-white" data-testid="nda-template-new-type">
                        {NDA_TYPES.map((t) => <option key={t} value={t}>{t} NDA</option>)}
                    </select>
                </div>
                <div className="col-span-2">
                    <Label>Default Purpose Statement</Label>
                    <Textarea rows={2} value={form.default_purpose} onChange={(e) => setForm((f) => ({ ...f, default_purpose: e.target.value }))} data-testid="nda-template-new-purpose" />
                </div>
                <div className="col-span-2 border-t border-slate-200 pt-3">
                    <div className="text-xs text-slate-500 mb-2">Starter clause — you can add more on the editor page.</div>
                </div>
                <div>
                    <Label>First Clause Heading</Label>
                    <Input value={form.first_clause_heading} onChange={(e) => setForm((f) => ({ ...f, first_clause_heading: e.target.value }))} data-testid="nda-template-new-clause-heading" />
                </div>
                <div className="col-span-2">
                    <Label>First Clause Body</Label>
                    <Textarea rows={4} value={form.first_clause_body} onChange={(e) => setForm((f) => ({ ...f, first_clause_body: e.target.value }))} data-testid="nda-template-new-clause-body" />
                </div>
            </div>
            <DialogFooter>
                <FormDialogCancel data-testid="nda-template-new-cancel">Cancel</FormDialogCancel>
                <Button onClick={submit} disabled={submitting} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="nda-template-new-submit">
                    {submitting ? "Creating…" : "Create Draft"}
                </Button>
            </DialogFooter>
        </FormDialog>
    );
}

export default function NDATemplateMaster() {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [typeFilter, setTypeFilter] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [newOpen, setNewOpen] = useState(false);

    const load = async () => {
        try {
            const params = { include_inactive: true };
            if (typeFilter) params.nda_type = typeFilter;
            if (statusFilter) params.status = statusFilter;
            const { data } = await api.get("/billing/nda-template/list", { params });
            setItems(data.items || []);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load templates"));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, [typeFilter, statusFilter]);

    const clone = async (row) => {
        try {
            const { data } = await api.post(`/billing/nda-template/${row.id}/clone`, {});
            toast.success(`Cloned to v${data.version} (Draft)`);
            navigate(`/admin/nda-templates/${data.id}`);
        } catch (err) {
            toast.error(formatApiError(err, "Clone failed"));
        }
    };

    const publish = async (row) => {
        if (!window.confirm(
            `Publish ${row.template_code} v${row.version}?  ` +
            `This will retire the currently-published version (if any) and become the default template for future ${row.nda_type} NDAs.`,
        )) return;
        try {
            const { data } = await api.post(`/billing/nda-template/${row.id}/publish`, { remarks: "" });
            toast.success(`Published ${data.template_code} v${data.version}`);
            load();
        } catch (err) {
            toast.error(formatApiError(err, "Publish failed"));
        }
    };

    const retire = async (row) => {
        if (!window.confirm(`Retire ${row.template_code} v${row.version}?  Existing NDAs continue to reference the frozen snapshot; no impact on historical documents.`)) return;
        try {
            const { data } = await api.post(`/billing/nda-template/${row.id}/retire`, { remarks: "" });
            toast.success(`Retired ${data.template_code} v${data.version}`);
            load();
        } catch (err) {
            toast.error(formatApiError(err, "Retire failed"));
        }
    };

    return (
        <div data-testid="nda-template-master-page">
            <PageHeader
                eyebrow="Admin · NDA Configuration"
                title="NDA Templates"
                description="Versioned clause templates that drive the rendered NDA document.  Published templates that have been referenced by an NDA become fully immutable — Clone as New Version to evolve legal wording."
                actions={
                    <div className="flex items-center gap-2">
                        <Link to="/admin/nda-items">
                            <Button variant="outline" className="gap-1.5" data-testid="nda-template-goto-items">
                                <Package size={14} /> NDA Items
                            </Button>
                        </Link>
                        <Button
                            onClick={() => setNewOpen(true)}
                            className="bg-slate-900 hover:bg-slate-800 text-white gap-1.5"
                            data-testid="nda-template-new-btn"
                        >
                            <Plus size={16} weight="bold" /> New Template
                        </Button>
                    </div>
                }
            />
            <div className="px-6 sm:px-8 py-8">
                <div className="flex flex-wrap items-center gap-2 mb-5" data-testid="nda-template-filters">
                    <div className="reedos-eyebrow flex items-center gap-1.5"><Funnel size={12} /> Filter</div>
                    <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="h-9 px-3 text-sm border border-slate-200 rounded-md bg-white" data-testid="nda-template-type-filter">
                        <option value="">All types</option>
                        {NDA_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                    <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="h-9 px-3 text-sm border border-slate-200 rounded-md bg-white" data-testid="nda-template-status-filter">
                        <option value="">All statuses</option>
                        <option value="Draft">Draft</option>
                        <option value="Published">Published</option>
                        <option value="Retired">Retired</option>
                    </select>
                </div>
                {loading ? (
                    <div className="text-sm text-slate-500">Loading templates…</div>
                ) : items.length === 0 ? (
                    <EmptyState
                        icon={<Scales size={40} weight="thin" />}
                        title="No templates match the filter"
                        description="Create a Draft or clear the filters."
                    />
                ) : (
                    <div className="reedos-card overflow-hidden">
                        <table className="w-full text-sm" data-testid="nda-template-table">
                            <thead className="bg-slate-50 border-b border-slate-200">
                                <tr className="text-left">
                                    <th className="px-4 py-3 reedos-eyebrow">Code</th>
                                    <th className="px-4 py-3 reedos-eyebrow">Version</th>
                                    <th className="px-4 py-3 reedos-eyebrow">Name</th>
                                    <th className="px-4 py-3 reedos-eyebrow">Type</th>
                                    <th className="px-4 py-3 reedos-eyebrow">Clauses</th>
                                    <th className="px-4 py-3 reedos-eyebrow">Status</th>
                                    <th className="px-4 py-3 reedos-eyebrow">Published</th>
                                    <th className="px-4 py-3 reedos-eyebrow">In Use</th>
                                    <th className="px-4 py-3"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((row) => {
                                    const badge = STATUS_BADGE_MAP[row.status] || { status: row.status };
                                    return (
                                        <tr key={row.id} className="border-t border-slate-100 reedos-row-hover" data-testid={`nda-template-row-${row.id}`}>
                                            <td className="px-4 py-3 font-mono text-xs">{row.template_code}</td>
                                            <td className="px-4 py-3 text-xs font-semibold">v{row.version}</td>
                                            <td className="px-4 py-3 text-slate-900">{row.template_name}</td>
                                            <td className="px-4 py-3 text-xs uppercase tracking-wide">{row.nda_type}</td>
                                            <td className="px-4 py-3 text-xs text-slate-600">{(row.clauses || []).length}</td>
                                            <td className="px-4 py-3"><StatusBadge {...badge}>{row.status}</StatusBadge></td>
                                            <td className="px-4 py-3 text-xs text-slate-500">{row.published_at ? formatDate(row.published_at) : "—"}</td>
                                            <td className="px-4 py-3 text-xs">
                                                {row.in_use ? <span className="text-amber-800 font-medium">Yes</span> : <span className="text-slate-400">No</span>}
                                            </td>
                                            <td className="px-4 py-3 text-right whitespace-nowrap">
                                                <button onClick={() => navigate(`/admin/nda-templates/${row.id}`)} className="text-slate-700 hover:text-slate-900 mr-3" data-testid={`nda-template-open-${row.id}`} title="Open">
                                                    <ArrowRight size={14} />
                                                </button>
                                                {row.status === "Draft" && (
                                                    <button onClick={() => publish(row)} className="text-emerald-700 hover:text-emerald-900 mr-3" data-testid={`nda-template-publish-${row.id}`} title="Publish">
                                                        <Sparkle size={14} />
                                                    </button>
                                                )}
                                                <button onClick={() => clone(row)} className="text-slate-700 hover:text-slate-900 mr-3" data-testid={`nda-template-clone-${row.id}`} title="Clone as new version">
                                                    <Copy size={14} />
                                                </button>
                                                {row.status !== "Retired" && (
                                                    <button onClick={() => retire(row)} className="text-rose-700 hover:text-rose-900" data-testid={`nda-template-retire-${row.id}`} title="Retire">
                                                        <Prohibit size={14} />
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
            <NewTemplateDialog open={newOpen} onOpenChange={setNewOpen} onCreated={(t) => navigate(`/admin/nda-templates/${t.id}`)} />
        </div>
    );
}
